import React from 'react'
import { defineMessages } from 'react-intl'

const LOCALES_WITH_CURRENCY_AFTER_AMOUNT = ['da', 'de', 'el', 'es', 'fr', 'nl-be', 'sv']

const OFFSET = 60000

const locale = defineMessages({
  free: {
    id: 'event-carousel.free',
    defaultMessage: 'Free',
  },
  dateTimeRange: {
    id: 'event-carousel.date-time-range',
    defaultMessage: '{dateRange} at {timeRange}',
  },
  plusMore: {
    id: 'event-carousel.plus-more',
    defaultMessage: ' + {count} more',
  },
})

const formattedDateRange = ({ start, end, intl, tz }) => {
  let result = intl.formatDate(start, { weekday: 'short', month: 'short', day: 'numeric' })

  if ((start.getDate() !== end.getDate()) || (start.getMonth() !== end.getMonth())) {
    result = `${result} - ${intl.formatDate(end, { weekday: 'short', month: 'short', day: 'numeric' })}`
  }

  if (tz) result = `${result} ${tz}`
  return result
}

const formattedTimeRange = ({ start, end, intl, tz }) => {
  let startTime = intl.formatTime(start).replace(':00', '')
  const period = startTime.split(' ')[1] // AM or PM
  const endTime = intl.formatTime(end).replace(':00', '')

  if (period && period === endTime.split(' ')[1]) {
    startTime = startTime.replace(` ${period}`, '')
  }

  let result = `${startTime}-${endTime}`
  if (tz) result = `${result} ${tz}`
  return result
}

const longFormattedTimeRange = ({ start, end, intl, tz }) => {
  let startTime = intl.formatTime(start)
  const period = startTime.split(' ')[1] // AM or PM
  const endTime = intl.formatTime(end)

  if (period && period === endTime.split(' ')[1]) {
    startTime = startTime.replace(` ${period}`, '')
  }

  let result = `${startTime}-${endTime}`
  if (tz) result = `${result} ${tz}`
  return result
}

export const formattedDateFullTimeRange = ({
  startAt,
  endAt,
  intl,
  totalCount,
  tz,
}) => {
  let start
  let end
  if (typeof startAt === 'string') {
    const startUtc = new Date(`${startAt}Z`)
    const endUtc = new Date(`${endAt}Z`)
    start = new Date(startUtc.getTime() + (startUtc.getTimezoneOffset() * OFFSET))
    end = new Date(endUtc.getTime() + (endUtc.getTimezoneOffset() * OFFSET))
  } else {
    start = startAt
    end = endAt
  }

  const dateRange = formattedDateRange({ start, end, intl })
  const timeRange = longFormattedTimeRange({ start, end, intl })
  let result = intl.formatMessage(locale.dateTimeRange, { dateRange, timeRange })
  if (tz) result = `${result} ${tz}`

  return totalCount === 1 ?
    result :
    <span>{result}&nbsp;{intl.formatMessage(locale.plusMore, { count: totalCount - 1 })}</span>
}

export const formattedDateTimeRange = ({
  startAt,
  endAt,
  intl,
  totalCount,
  tz,
}) => {
  let start
  let end
  if (typeof startAt === 'string') {
    const startUtc = new Date(`${startAt}Z`)
    const endUtc = new Date(`${endAt}Z`)
    start = new Date(startUtc.getTime() + (startUtc.getTimezoneOffset() * OFFSET))
    end = new Date(endUtc.getTime() + (endUtc.getTimezoneOffset() * OFFSET))
  } else {
    start = startAt
    end = endAt
  }

  const dateRange = formattedDateRange({ start, end, intl })
  const timeRange = formattedTimeRange({ start, end, intl })
  let result = intl.formatMessage(locale.dateTimeRange, { dateRange, timeRange })
  if (tz) result = `${result} ${tz}`


  return totalCount === 1 ?
    result :
    <span>{result}&nbsp;{intl.formatMessage(locale.plusMore, { count: totalCount - 1 })}</span>
}

export const formattedMultipleDateRange = ({
  startAt,
  endAt,
  intl,
  totalCount,
  tz,
}) => {
  let start
  let end
  if (typeof startAt === 'string') {
    const startUtc = new Date(`${startAt}Z`)
    const endUtc = new Date(`${endAt}Z`)
    start = new Date(startUtc.getTime() + (startUtc.getTimezoneOffset() * OFFSET))
    end = new Date(endUtc.getTime() + (endUtc.getTimezoneOffset() * OFFSET))
  } else {
    start = startAt
    end = endAt
  }

  let result = formattedDateRange({ start, end, intl })
  if (tz) result = `${result} ${tz}`

  return totalCount === 1 ?
    result :
    <span>{result}&nbsp;{intl.formatMessage(locale.plusMore, { count: totalCount - 1 })}</span>
}

export const formattedTimeOnlyRange = ({ startAt, endAt, intl, tz }) => {
  let start
  let end
  if (typeof startAt === 'string') {
    const startUtc = new Date(`${startAt}Z`)
    const endUtc = new Date(`${endAt}Z`)
    start = new Date(startUtc.getTime() + (startUtc.getTimezoneOffset() * OFFSET))
    end = new Date(endUtc.getTime() + (endUtc.getTimezoneOffset() * OFFSET))
  } else {
    start = startAt
    end = endAt
  }

  return longFormattedTimeRange({ start, end, intl, tz })
}

export const formattedPriceRange = ({
  minPrice,
  maxPrice,
  currency,
  intl,
}) => {
  if (minPrice === null) return ''

  const formattedMinPrice = minPrice ?
    intl.formatNumber(minPrice, { style: 'currency', currency })
    :
    intl.formatMessage(locale.free)
  const formattedMaxPrice = maxPrice ?
    intl.formatNumber(maxPrice, { style: 'currency', currency })
    :
    intl.formatMessage(locale.free)

  // CA$10.00 or Free
  if (minPrice === maxPrice || !maxPrice) {
    return formattedMinPrice
  }

  // 10.00-20.00CA$ or Free-20.00CA$
  if (LOCALES_WITH_CURRENCY_AFTER_AMOUNT.includes(intl.locale)) {
    if (minPrice) {
      const minPriceWithoutCurrency = intl.formatNumber(
        minPrice,
        { minimumFractionDigits: 0, maximumFractionDigits: 2 },
      )
      return `${minPriceWithoutCurrency}-${formattedMaxPrice}`
    }
    return `${formattedMinPrice}-${formattedMaxPrice}`
  }

  // CA$10.00-20.00
  if (minPrice) {
    if (maxPrice) {
      const maxPriceWithoutCurrency = intl.formatNumber(
        maxPrice,
        { minimumFractionDigits: 0, maximumFractionDigits: 2 },
      )
      return `${formattedMinPrice}-${maxPriceWithoutCurrency}`
    }
    return `${formattedMinPrice}-${formattedMaxPrice}`
  }

  return `${formattedMinPrice}-${formattedMaxPrice}`
}

export const formattedPriceRangeEDP = ({
  minPrice,
  maxPrice,
  currency,
  intl,
}) => {
  if (minPrice === null) return ['']

  const formattedMinPrice = minPrice ?
    intl.formatNumber(minPrice, { style: 'currency', currency })
    :
    intl.formatMessage(locale.free)
  const formattedMaxPrice = maxPrice ?
    intl.formatNumber(maxPrice, { style: 'currency', currency })
    :
    intl.formatMessage(locale.free)

  // CA$10.00 or Free
  if (minPrice === maxPrice || !maxPrice) {
    return [formattedMinPrice]
  }

  // 10.00-20.00CA$ or Free-20.00CA$
  if (LOCALES_WITH_CURRENCY_AFTER_AMOUNT.includes(intl.locale)) {
    return [`${minPrice ? formattedMinPrice.match(/\d+[,.]\d+/)[0] : formattedMinPrice}`, `${formattedMaxPrice}`]
  }

  // CA$10.00-20.00
  return [formattedMinPrice, formattedMaxPrice]
}
